/**
 * ServicesAccordion - Interactive hover-based image accordion for services
 *
 * Inspired by 21st.dev/r/minhxthanh/interactive-image-accordion
 * Replaces the grid layout with an animated accordion that expands on hover.
 *
 * PRESERVES:
 * - Tag data aggregation from portfolio images
 * - Click to filter gallery functionality
 * - Image caching to prevent flickering
 * - Loading state with skeletons
 */

import { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useArtists } from "@/hooks";
import { cn, toTitleCase } from "@/lib/utils";

interface ServicesAccordionProps {
  onTagClick?: (tag: string) => void;
  selectedTag?: string | null;
  maxItems?: number;
}

interface TagData {
  fullImages: string[];
  thumbImages: string[];
  count: number;
}

interface AccordionItemProps {
  tag: string;
  imageUrl: string;
  count: number;
  isActive: boolean;
  isSelected: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

function AccordionItem({
  tag,
  imageUrl,
  count,
  isActive,
  isSelected,
  onMouseEnter,
  onClick,
}: AccordionItemProps) {
  const [imageError, setImageError] = useState(false);

  return (
    <button
      className={cn(
        "accordion-panel focus-ring relative h-[400px] overflow-hidden rounded-xl",
        isActive ? "flex-[4]" : "flex-1",
        isSelected && "ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-bg)]"
      )}
      onMouseEnter={onMouseEnter}
      onFocus={onMouseEnter}
      onClick={onClick}
      aria-pressed={isSelected}
    >
      <img
        src={imageError ? "/static/images/decorations/banners/KomikoBannerFullQuality.png" : imageUrl}
        alt={toTitleCase(tag)}
        className={cn("accordion-panel-image absolute inset-0 w-full h-full object-cover", isActive && "is-active")}
        onError={() => setImageError(true)}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

      <motion.div
        className={cn(
          "absolute bottom-0 left-0 right-0 p-4 transition-all duration-500",
          isActive ? "text-center" : "text-left"
        )}
        animate={{
          opacity: isActive ? 1 : 0.9,
        }}
      >
        <motion.span
          className={cn(
            "block font-semibold text-white transition-all duration-500",
            isActive ? "text-2xl" : "text-sm"
          )}
          style={{
            writingMode: isActive ? "horizontal-tb" : "vertical-rl",
            transform: isActive ? "rotate(0deg)" : "rotate(180deg)",
          }}
        >
          {toTitleCase(tag)}
        </motion.span>

        <motion.span
          className="text-white/70 text-sm mt-1 block"
          animate={{
            opacity: isActive ? 1 : 0,
            height: isActive ? "auto" : 0,
          }}
          transition={{ duration: 0.3 }}
        >
          {count} artwork{count !== 1 ? "s" : ""}
        </motion.span>
      </motion.div>

      {isSelected && (
        <motion.div
          className="absolute top-2 right-2 bg-[var(--color-primary)] text-[var(--color-bg)] px-2 py-1 rounded-full text-xs font-medium"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
        >
          Active
        </motion.div>
      )}
    </button>
  );
}

function OverflowMiniCard({
  tag,
  imageUrl,
  count,
  isSelected,
  onClick,
}: {
  tag: string;
  imageUrl: string;
  count: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "pressable focus-ring relative h-20 min-w-[150px] flex-1 rounded-lg overflow-hidden border",
        isSelected
          ? "border-[var(--color-primary)] ring-2 ring-[var(--color-primary)]"
          : "border-[var(--color-border)] hover:border-[var(--color-primary)]"
      )}
    >
      <img src={imageUrl} alt={toTitleCase(tag)} className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <span className="block text-white font-medium text-sm">{toTitleCase(tag)}</span>
        <span className="text-white/60 text-xs">{count} artwork{count !== 1 ? 's' : ''}</span>
      </div>
    </button>
  );
}

export function ServicesAccordion({
  onTagClick,
  selectedTag,
  maxItems = 8,
}: ServicesAccordionProps) {
  const { data: artists, isLoading } = useArtists();
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullImageCache, setFullImageCache] = useState<Record<string, string>>({});
  const [thumbImageCache, setThumbImageCache] = useState<Record<string, string>>({});

  // Aggregate tag data from all portfolio images (SAFE-ONLY)
  const tagData = useMemo(() => {
    const data: Record<string, TagData> = {};

    artists?.forEach((artist) => {
      artist.portfolio?.forEach((img) => {
        // Only include safe-rated images in Services section
        if (img.contentRating !== "safe") return;

        const tags = img.tags || [];
        const fullUrl = img.url;
        const thumbUrl = img.thumbnail || img.url;

        tags.forEach((rawTag) => {
          const tag = rawTag.trim().toLowerCase();
          if (!data[tag]) data[tag] = { fullImages: [], thumbImages: [], count: 0 };
          data[tag].fullImages.push(fullUrl);
          data[tag].thumbImages.push(thumbUrl);
          data[tag].count++;
        });
      });

      // Fallback: use artist tags if no portfolio tags
      if (!artist.portfolio?.length && artist.tags?.length) {
        artist.tags.forEach((rawTag) => {
          const tag = rawTag.trim().toLowerCase();
          if (!data[tag]) data[tag] = { fullImages: [], thumbImages: [], count: 0 };
          if (artist.coverImage) {
            data[tag].fullImages.push(artist.coverImage);
            data[tag].thumbImages.push(artist.coverImage);
          }
        });
      }
    });

    return data;
  }, [artists]);

  // Sort all tags by popularity
  const allSortedTags = useMemo(() => {
    return Object.entries(tagData)
      .filter(([, data]) => data.fullImages.length > 0)
      .sort((a, b) => b[1].count - a[1].count);
  }, [tagData]);

  // Split into accordion tags and overflow tags
  const accordionSlots = maxItems;
  const accordionTags = allSortedTags.slice(0, accordionSlots);
  const overflowTags = allSortedTags.slice(accordionSlots);

  // Cache deduplicated images on mount (popular tags pick first)
  useEffect(() => {
    const fullCache: Record<string, string> = {};
    const thumbCache: Record<string, string> = {};
    const usedFullImages = new Set<string>();
    const usedThumbImages = new Set<string>();

    allSortedTags.forEach(([tag, data]) => {
      const uniqueFull = data.fullImages.filter((img) => !usedFullImages.has(img));
      const fullImg =
        uniqueFull.length > 0
          ? uniqueFull[Math.floor(Math.random() * uniqueFull.length)]
          : data.fullImages[Math.floor(Math.random() * data.fullImages.length)];
      if (fullImg) {
        usedFullImages.add(fullImg);
        fullCache[tag] = fullImg;
      }

      const uniqueThumb = data.thumbImages.filter((img) => !usedThumbImages.has(img));
      const thumbImg =
        uniqueThumb.length > 0
          ? uniqueThumb[Math.floor(Math.random() * uniqueThumb.length)]
          : data.thumbImages[Math.floor(Math.random() * data.thumbImages.length)];
      if (thumbImg) {
        usedThumbImages.add(thumbImg);
        thumbCache[tag] = thumbImg;
      }
    });

    setFullImageCache(fullCache);
    setThumbImageCache(thumbCache);
  }, [allSortedTags.length]);

  if (isLoading) {
    return (
      <section id="services" className="py-16 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-[var(--font-display)] text-2xl text-[var(--color-text)] text-center mb-2">
            Our <em className="text-[var(--color-primary)]">Services</em>
          </h2>
          <p className="text-center text-[var(--color-text-muted)] mb-8 text-sm">
            Hover to explore, click to filter artwork
          </p>
          <div className="flex gap-2 h-[400px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-xl bg-[var(--color-bg-card)] animate-pulse",
                  i === 0 ? "flex-[4]" : "flex-1"
                )}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (accordionTags.length === 0) {
    return null;
  }

  return (
    <section id="services" className="py-16 bg-[var(--color-bg)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-[var(--font-display)] text-2xl text-[var(--color-text)] text-center mb-2">
          Our <em className="text-[var(--color-primary)]">Services</em>
        </h2>
        <p className="text-center text-[var(--color-text-muted)] mb-8 text-sm">
          Hover to explore, click to filter artwork
        </p>

        {/* Desktop: Accordion layout */}
        <div className="hidden md:flex gap-2">
          {/* Dynamic Service Items */}
          {accordionTags.map(([tag, data], index) => (
            <AccordionItem
              key={tag}
              tag={tag}
              imageUrl={fullImageCache[tag] || data.fullImages[0]}
              count={data.count}
              isActive={activeIndex === index}
              isSelected={selectedTag === tag}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => onTagClick?.(tag)}
            />
          ))}
        </div>

        {/* Desktop: Overflow mini cards */}
        {overflowTags.length > 0 && (
          <div className="hidden md:flex flex-wrap gap-2 mt-4">
            {overflowTags.map(([tag, data]) => (
              <OverflowMiniCard
                key={tag}
                tag={tag}
                imageUrl={thumbImageCache[tag] || data.thumbImages[0]}
                count={data.count}
                isSelected={selectedTag === tag}
                onClick={() => onTagClick?.(tag)}
              />
            ))}
          </div>
        )}

        {/* Mobile: Grid layout (fallback) */}
        <div className="grid grid-cols-2 gap-4 md:hidden">
          {/* Service Cards (accordion-level tags) */}
          {accordionTags.map(([tag, data]) => (
            <button
              key={tag}
              onClick={() => onTagClick?.(tag)}
              className={cn(
                "pressable focus-ring group relative aspect-[4/3] rounded-xl overflow-hidden border",
                selectedTag === tag
                  ? "border-[var(--color-primary)] ring-2 ring-[var(--color-primary)] ring-offset-2"
                  : "border-[var(--color-border)] hover:border-[var(--color-primary)]",
                "bg-[var(--color-bg-card)] shadow-md"
              )}
            >
              <img
                src={fullImageCache[tag] || data.fullImages[0]}
                alt={toTitleCase(tag)}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <span className="block text-white font-semibold">{toTitleCase(tag)}</span>
                <span className="text-white/70 text-sm">
                  {data.count} artwork{data.count !== 1 ? "s" : ""}
                </span>
              </div>
            </button>
          ))}
        </div>

        {/* Mobile: Overflow mini cards */}
        {overflowTags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 md:hidden">
            {overflowTags.map(([tag, data]) => (
              <OverflowMiniCard
                key={tag}
                tag={tag}
                imageUrl={thumbImageCache[tag] || data.thumbImages[0]}
                count={data.count}
                isSelected={selectedTag === tag}
                onClick={() => onTagClick?.(tag)}
              />
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <p className="text-sm text-[var(--color-text-muted)]">
            Don't see what you need?{" "}
            <strong className="text-[var(--color-text)]">Send us a message</strong>{" "}
            and we'll figure it out!
          </p>
        </div>
      </div>
    </section>
  );
}
